import { Component, ElementRef, HostListener, OnDestroy, ViewChild, output, signal } from '@angular/core';

const ZOOM_FACTOR = 2.5;
const LENS_WIDTH = 450;
const LENS_HEIGHT = 300;
const CLONE_REFRESH_DELAY_MS = 200;

@Component({
  selector: 'app-magnifier-widget',
  imports: [],
  templateUrl: './magnifier-widget.html',
  styleUrl: './magnifier-widget.css',
})
export class MagnifierWidgetComponent implements OnDestroy {
  @ViewChild('lensContent', { static: true }) private lensContentRef!: ElementRef<HTMLDivElement>;

  protected readonly isActive = signal(false);
  protected readonly isLensVisible = signal(false);
  protected readonly lensPosition = signal({ x: 0, y: 0 });
  protected lensWidth = LENS_WIDTH;
  protected lensHeight = LENS_HEIGHT;
  /** Emits whenever the magnifier is switched on (true) or off (false). */
  readonly activeChange = output<boolean>();

  private cloneEl: HTMLElement | null = null;
  private observer: MutationObserver | null = null;
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Touch listeners are attached manually so touchmove can be non-passive: while the
   * magnifier is on, dragging a finger moves the lens instead of scrolling the page.
   */
  private readonly onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (touch) {
      this.moveLens(touch.clientX, touch.clientY);
    }
  };

  private readonly onTouchMove = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    this.moveLens(touch.clientX, touch.clientY);
  };

  protected toggleMagnifier(): void {
    this.isActive.update((value) => !value);

    if (this.isActive()) {
      this.startCloning();
    } else {
      this.stopCloning();
      this.isLensVisible.set(false);
    }

    this.activeChange.emit(this.isActive());
  }

  @HostListener('document:mousemove', ['$event'])
  protected onDocumentMouseMove(event: MouseEvent): void {
    this.moveLens(event.clientX, event.clientY);
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.updateLensSize();
  }

  private moveLens(clientX: number, clientY: number): void {
    if (!this.isActive() || !this.cloneEl) {
      return;
    }

    const halfWidth = this.lensWidth / 2;
    const halfHeight = this.lensHeight / 2;
    const pageX = clientX + window.scrollX;
    const pageY = clientY + window.scrollY;

    this.cloneEl.style.left = `${halfWidth - pageX * ZOOM_FACTOR}px`;
    this.cloneEl.style.top = `${halfHeight - pageY * ZOOM_FACTOR}px`;

    this.lensPosition.set({ x: clientX - halfWidth, y: clientY - halfHeight });
    this.isLensVisible.set(true);
  }

  @HostListener('window:mouseout', ['$event'])
  protected onWindowMouseOut(event: MouseEvent): void {
    if (!event.relatedTarget) {
      this.isLensVisible.set(false);
    }
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    this.isLensVisible.set(false);
  }

  ngOnDestroy(): void {
    this.stopCloning();
  }

  /** Keeps the lens within small (tablet/mobile) viewports. */
  private updateLensSize(): void {
    this.lensWidth = Math.min(LENS_WIDTH, Math.round(window.innerWidth * 0.8));
    this.lensHeight = Math.min(LENS_HEIGHT, Math.round(window.innerHeight * 0.4));
  }

  private startCloning(): void {
    this.updateLensSize();
    this.renderClone();

    document.addEventListener('touchstart', this.onTouchStart, { passive: true });
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });

    this.observer = new MutationObserver(() => this.scheduleRefresh());
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }

  private stopCloning(): void {
    document.removeEventListener('touchstart', this.onTouchStart);
    document.removeEventListener('touchmove', this.onTouchMove);

    this.observer?.disconnect();
    this.observer = null;

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    this.cloneEl?.remove();
    this.cloneEl = null;
  }

  private scheduleRefresh(): void {
    if (this.refreshTimeout) {
      return;
    }

    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      this.renderClone();
    }, CLONE_REFRESH_DELAY_MS);
  }

  private renderClone(): void {
    const container = this.lensContentRef.nativeElement;
    const previousLeft = this.cloneEl?.style.left ?? '0px';
    const previousTop = this.cloneEl?.style.top ?? '0px';

    this.cloneEl?.remove();

    const clone = document.body.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone
      .querySelectorAll('app-magnifier-widget, app-calculator-widget')
      .forEach((element) => element.remove());
    clone.querySelectorAll('input, textarea, select').forEach((element) => {
      element.setAttribute('disabled', 'true');
    });

    clone.style.position = 'absolute';
    clone.style.margin = '0';
    clone.style.width = `${document.documentElement.clientWidth}px`;
    clone.style.transformOrigin = '0 0';
    clone.style.transform = `scale(${ZOOM_FACTOR})`;
    clone.style.pointerEvents = 'none';
    clone.style.left = previousLeft;
    clone.style.top = previousTop;

    container.appendChild(clone);
    this.cloneEl = clone;
  }
}
